import { useEffect, useState } from 'react';
import { useAuthStore } from '@/auth/authStore';
import { useUIStore } from '@/stores/uiStore';
import { useMatchStore } from '@/stores/matchStore';
import { BackButton } from '@/components/BackButton';
import { useT } from '@/i18n/useI18n';
import { audio } from '@/audio/AudioService';
import { shareGame } from '@/rewards/questApi';
import {
  challengeApi,
  type FriendChallengeDto,
} from '@/challenges/challengeApi';
import { gameCenterChallengeShareUrl } from '@/challenges/gameCenterChallenge';

function formatTime(ms: number): string {
  return `${(ms / 1000).toFixed(2)}s`;
}

export function FriendChallengeScreen() {
  const t = useT();
  const go = useUIStore((state) => state.go);
  const code = useUIStore((state) => state.challengeCode);
  const me = useAuthStore((state) => state.user);
  const startChallenge = useMatchStore((state) => state.startChallengeMatch);
  const [challenge, setChallenge] = useState<FriendChallengeDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    setError(null);
    challengeApi.get(code)
      .then(({ challenge: next }) => setChallenge(next))
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : String(reason)));
  }, [code]);

  async function play() {
    if (!challenge || !me || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { run } = await challengeApi.begin(challenge.code);
      startChallenge(run, me.id);
      audio.play('match.found');
      go('match');
    } catch (reason: unknown) {
      audio.play('ui.error');
      setError(reason instanceof Error ? reason.message : String(reason));
      setBusy(false);
    }
  }

  async function share() {
    if (!challenge) return;
    const url = await gameCenterChallengeShareUrl(challenge.code, challenge.targetDurationMs);
    await shareGame(
      t('challenge.share_text', {
        name: challenge.creator.name,
        time: formatTime(challenge.targetDurationMs),
      }),
      url,
    );
  }

  return (
    <div className="screen scanlines">
      <div className="hex-bg" />
      <div style={{ padding: '14px 18px 0', position: 'relative', zIndex: 1 }}>
        <BackButton onClick={() => go('friends')} />
        <div className="tag" style={{ color: 'var(--c-purple)', marginTop: 14 }}>
          {t('challenge.ghost_duel')}
        </div>
        <div className="display" style={{ fontSize: 30, color: 'var(--c-yellow)', marginTop: 4 }}>
          {t('challenge.title')}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 18px 100px', position: 'relative', zIndex: 1 }}>
        {!challenge && !error && <div className="card" style={{ padding: 20 }}>{t('common.loading')}</div>}
        {error && (
          <div className="card" style={{ padding: 18, color: 'var(--c-red)', textAlign: 'center' }}>
            {t('challenge.error_default')} <span className="mono">({error})</span>
          </div>
        )}
        {challenge && (
          <>
            <div
              className="card"
              style={{
                padding: 20,
                textAlign: 'center',
                borderColor: 'rgba(244,199,36,.55)',
                boxShadow: '0 0 30px rgba(244,199,36,.12)',
              }}
            >
              <div style={{ width: 72, height: 72, margin: '0 auto 12px', borderRadius: '50%', padding: 3, background: 'linear-gradient(135deg,var(--c-yellow),var(--c-purple))' }}>
                {challenge.creator.avatarUrl ? (
                  <img src={challenge.creator.avatarUrl} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div className="display" style={{ display: 'grid', placeItems: 'center', width: '100%', height: '100%', borderRadius: '50%', background: '#12102d', fontSize: 28 }}>
                    {challenge.creator.name.slice(0, 1).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="display" style={{ fontSize: 21 }}>{challenge.creator.name}</div>
              <div style={{ color: 'var(--ink-muted)', fontSize: 12, marginTop: 4 }}>
                {t('challenge.invites_you')}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 18 }}>
                <Stat label={t('challenge.target')} value={formatTime(challenge.targetDurationMs)} color="var(--c-yellow)" />
                <Stat label={t('challenge.accuracy')} value={`${challenge.targetAccuracy}%`} color="var(--c-cyan)" />
                <Stat label={t('challenge.combo')} value={`×${challenge.targetMaxCombo}`} color="var(--c-purple)" />
              </div>
            </div>

            <div className="card" style={{ padding: 14, marginTop: 12 }}>
              <div className="tag">{t('challenge.how_it_works')}</div>
              <div style={{ marginTop: 8, color: 'var(--ink-muted)', fontSize: 12, lineHeight: 1.55 }}>
                {t('challenge.how_it_works_body')}
              </div>
            </div>

            {challenge.attempt?.finishedAt && (
              <div className="card" style={{ padding: 14, marginTop: 12, borderColor: challenge.attempt.won ? 'var(--c-cyan)' : 'var(--c-red)' }}>
                <div className="display" style={{ color: challenge.attempt.won ? 'var(--c-cyan)' : 'var(--c-red)', textAlign: 'center' }}>
                  {challenge.attempt.won ? t('challenge.you_won') : t('challenge.you_lost')}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gap: 9, marginTop: 16 }}>
              {challenge.canPlay && (
                <button className="btn btn-primary" disabled={busy} onClick={() => void play()}>
                  {busy ? t('common.loading') : `⚡ ${t('challenge.accept')}`}
                </button>
              )}
              {challenge.mine && challenge.status !== 'expired' && (
                <button className="btn btn-primary" onClick={() => void share()}>
                  ↗ {t('challenge.share')}
                </button>
              )}
              <button className="btn btn-ghost" onClick={() => go('friends')}>
                {t('common.back')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ padding: '10px 4px', borderRadius: 10, background: 'rgba(0,0,0,.3)', border: '1px solid var(--hairline)' }}>
      <div className="tag" style={{ fontSize: 8 }}>{label}</div>
      <div className="display" style={{ color, fontSize: 18, marginTop: 5 }}>{value}</div>
    </div>
  );
}
